import React, { useCallback } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import {
	getPartnerCompanyEvent,
	getPartnerCompanyEventUsers,
	getPartnerCompanyEventRoles,
	getPartnerCompanyRoles,
	linkUserToPartnerCompanyEvent,
	removeUserFromPartnerCompanyEvent,
} from 'src/api/partner';
import UsersTab from 'src/components/partner-portal/UsersTab';
import { getVerifyPermission } from '@repo/shared/helpers';
import { PERMISSIONS } from '@repo/shared/domain';
import { EventEmitter } from '@repo/shared/helpers';
import { LinkData, InviteData, User } from '@repo/shared/domain';
import { Spinner } from '@repo/components';
import Alert from '@mui/material/Alert';
import { constructQuery } from '@repo/shared/helpers';
import { useBreadcrumbLevel } from '@repo/components/hooks';

const defaultCountry = 'BE';

export const Route = createFileRoute('/partner_/$partnerId/events/$eventId/users')({
	component: RouteComponent,
	validateSearch: (search: Record<string, unknown>) => ({
		search: (search.search as string) || '',
		page: (search.page as number) || 0,
		sort: (search.sort as string) || 'firstName:asc',
	}),
});

function RouteComponent() {
	const { user: authenticatedUser, loading: userLoading } = useAuthenticatedUser();
	const { partnerId, eventId } = Route.useParams();
	useBreadcrumbLevel(3, 'Users');
	const { search, page, sort } = Route.useSearch();
	const queryClient = useQueryClient();
	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);

	// Get event data
	const {
		data: event,
		isLoading: eventLoading,
		error: eventError,
	} = useQuery({
		queryKey: ['partner-company-event', partnerId, eventId],
		queryFn: () => getPartnerCompanyEvent(partnerId, eventId),
		enabled: !userLoading && !!authenticatedUser,
	});

	// Get users for this event
	const { data: usersResponse, isLoading: usersLoading } = useQuery({
		queryKey: ['partner-company-event-users', partnerId, eventId, { search, page, sort }],
		queryFn: () =>
			getPartnerCompanyEventUsers(partnerId, eventId, constructQuery({ search, page: page.toString(), sort })),
		enabled: !userLoading && !!authenticatedUser && !!event,
	});

	// Get event roles (preferred) or fallback to group roles
	const { data: eventRoles, isLoading: eventRolesLoading } = useQuery({
		queryKey: ['partner-company-event-roles', partnerId, eventId],
		queryFn: () => getPartnerCompanyEventRoles(partnerId, eventId),
		enabled: !userLoading && !!authenticatedUser && !!event,
	});

	const { data: groupRoles, isLoading: groupRolesLoading } = useQuery({
		queryKey: ['partner-company-roles', partnerId],
		queryFn: () => getPartnerCompanyRoles(partnerId),
		enabled: !userLoading && !!authenticatedUser && !eventRoles && !!event,
	});

	const roles = eventRoles || groupRoles || [];
	const isOwnGroupEvent = event?.groupIds?.includes(partnerId) ?? false;

	const handleReload = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: ['partner-company-event-users', partnerId, eventId] });
	}, [queryClient, partnerId, eventId]);

	const handleLinkUser = useCallback(
		async (linkData: LinkData) => {
			const { userId, roleIds } = linkData;

			if (!userId || event?.userIds?.includes(userId)) {
				return;
			}

			try {
				await linkUserToPartnerCompanyEvent(partnerId, eventId, { userId, roleIds });
				EventEmitter.emit('toast', {
					type: 'success',
					message: 'Changes saved',
				});
			} catch (error) {
				console.error(error);
				EventEmitter.emit('toast', {
					type: 'error',
					message: 'Error linking user to event',
				});
			}
			handleReload();
		},
		[eventId, partnerId, event?.userIds, handleReload],
	);

	const handleInviteUser = useCallback(
		async (data: InviteData) => {
			try {
				const response = await fetch(`/api/partner-portal/groups/${partnerId}/users`, {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify(data),
				});
				if (!response.ok) throw new Error('Failed to create user');
				const user = (await response.json()) as User;
				await handleLinkUser({ userId: user.id });
				return user;
			} catch (error) {
				EventEmitter.emit('toast', {
					type: 'error',
					message: (error as Error).message || 'Error adding user to event',
				});
				throw error;
			}
		},
		[partnerId, handleLinkUser],
	);

	const handleDeleteUser = useCallback(
		async (userId: string) => {
			try {
				await removeUserFromPartnerCompanyEvent(partnerId, eventId, userId);
				EventEmitter.emit('toast', {
					type: 'success',
					message: 'Changes saved',
				});
			} catch (_error) {
				EventEmitter.emit('toast', {
					type: 'error',
					message: 'Error',
				});
			}
			handleReload();
		},
		[eventId, partnerId, handleReload],
	);

	if (userLoading || eventLoading || usersLoading || eventRolesLoading || groupRolesLoading) {
		return (
			<div className="flex items-center justify-center h-full">
				<Spinner className="text-[#1E1E1E]" />
			</div>
		);
	}

	if (eventError || !event) {
		return (
			<div className="p-6">
				<Alert severity="error">Event not found</Alert>
			</div>
		);
	}

	// Show alert if inheritUsersFromGroups is true
	if (event?.type?.inheritUsersFromGroups) {
		return (
			<div className="p-6">
				<Alert severity="info" variant="filled">
					<strong>All Company Users Have Access</strong>
					<br />
					This event type is configured to inherit users from all company groups. All users belonging to the assigned
					companies will automatically have access to this event.
				</Alert>
			</div>
		);
	}

	return (
		<div className="p-6">
			<UsersTab
				onInvite={handleInviteUser}
				onDelete={handleDeleteUser}
				onReload={handleReload}
				onLink={handleLinkUser}
				event={event}
				roles={roles}
				authenticatedUser={authenticatedUser}
				users={usersResponse}
				readOnly
				permissions={{
					archive: isOwnGroupEvent && verifyPermission(PERMISSIONS.partnerCompanyEventsEdit),
					update: isOwnGroupEvent && verifyPermission(PERMISSIONS.partnerCompanyEventsEdit),
					create:
						isOwnGroupEvent &&
						verifyPermission(PERMISSIONS.partnerCompanyEventsEdit) &&
						verifyPermission(PERMISSIONS.partnerCompanyUsersCreate),
					canPrintBadges: false,
					canCreateWithoutEmail: false,
					isUserAdmin: false,
					canImport: false,
				}}
				addUserButtonState={{
					disabled: event.source === 'calendar',
					tooltip: 'This event does not allow adding users through this interface.',
				}}
				defaultCountry={defaultCountry}
				partnerId={partnerId}
				search={search}
				sort={sort}
			/>
		</div>
	);
}
